import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { demoUsers } from '@/mocks/demoData';
import type { User, UserRole } from '@/mocks/data';

export interface RolePermissions {
  dashboard: boolean;
  reception: {
    shifts: boolean;
    checkin: boolean;
    checkout: boolean;
    billing: boolean;
    cash: boolean;
    logs: boolean;
    reports: boolean;
  };
  bookings: {
    rooms: boolean;
    new: boolean;
    manage: boolean;
    guests: boolean;
    billing: boolean;
    reports: boolean;
  };
  inventory: {
    stock: boolean;
    purchases: boolean;
    vendors: boolean;
    reports: boolean;
  };
  canteen: {
    menu: boolean;
    billing: boolean;
    history: boolean;
    reports: boolean;
  };
  manageUsers: boolean;
}

export const BUILT_IN_ROLES: UserRole[] = ['admin', 'manager', 'receptionist', 'housekeeping', 'accountant'];

function createEmptyPermissions(): RolePermissions {
  return {
    dashboard: true,
    reception: { shifts: false, checkin: false, checkout: false, billing: false, cash: false, logs: false, reports: false },
    bookings: { rooms: false, new: false, manage: false, guests: false, billing: false, reports: false },
    inventory: { stock: false, purchases: false, vendors: false, reports: false },
    canteen: { menu: false, billing: false, history: false, reports: false },
    manageUsers: false,
  };
}

export const DEFAULT_PERMISSIONS: Record<string, RolePermissions> = {
  admin: {
    dashboard: true,
    reception: { shifts: true, checkin: true, checkout: true, billing: true, cash: true, logs: true, reports: true },
    bookings: { rooms: true, new: true, manage: true, guests: true, billing: true, reports: true },
    inventory: { stock: true, purchases: true, vendors: true, reports: true },
    canteen: { menu: true, billing: true, history: true, reports: true },
    manageUsers: true,
  },
  manager: {
    dashboard: true,
    reception: { shifts: true, checkin: true, checkout: true, billing: true, cash: true, logs: true, reports: true },
    bookings: { rooms: true, new: true, manage: true, guests: true, billing: true, reports: true },
    inventory: { stock: false, purchases: false, vendors: false, reports: false },
    canteen: { menu: false, billing: false, history: false, reports: false },
    manageUsers: false,
  },
  receptionist: {
    dashboard: true,
    reception: { shifts: false, checkin: true, checkout: true, billing: true, cash: false, logs: false, reports: false },
    bookings: { rooms: false, new: true, manage: false, guests: false, billing: false, reports: false },
    inventory: { stock: false, purchases: false, vendors: false, reports: false },
    canteen: { menu: false, billing: false, history: false, reports: false },
    manageUsers: false,
  },
  housekeeping: {
    dashboard: true,
    reception: { shifts: false, checkin: false, checkout: false, billing: false, cash: false, logs: false, reports: false },
    bookings: { rooms: true, new: false, manage: false, guests: false, billing: false, reports: false },
    inventory: { stock: true, purchases: false, vendors: false, reports: false },
    canteen: { menu: false, billing: false, history: false, reports: false },
    manageUsers: false,
  },
  accountant: {
    dashboard: true,
    reception: { shifts: false, checkin: false, checkout: false, billing: true, cash: false, logs: false, reports: true },
    bookings: { rooms: false, new: false, manage: false, guests: false, billing: true, reports: true },
    inventory: { stock: false, purchases: false, vendors: false, reports: true },
    canteen: { menu: false, billing: false, history: true, reports: true },
    manageUsers: false,
  },
};

const BUILT_IN_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  receptionist: 'Receptionist',
  housekeeping: 'Housekeeping',
  accountant: 'Accountant',
};

const ALL_PERMISSIONS_ZERO: RolePermissions = {
  dashboard: false,
  reception: { shifts: false, checkin: false, checkout: false, billing: false, cash: false, logs: false, reports: false },
  bookings: { rooms: false, new: false, manage: false, guests: false, billing: false, reports: false },
  inventory: { stock: false, purchases: false, vendors: false, reports: false },
  canteen: { menu: false, billing: false, history: false, reports: false },
  manageUsers: false,
};

export interface CustomRoleData {
  label: string;
  permissions: RolePermissions;
}

const PERM_STORAGE_KEY = 'hotelpro_permissions';
const CUSTOM_ROLES_STORAGE_KEY = 'hotelpro_custom_roles';

function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveToStorage<T>(key: string, data: T) {
  localStorage.setItem(key, JSON.stringify(data));
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => boolean;
  logout: () => void;
  permissions: RolePermissions | null;
  changePassword: (oldPassword: string, newPassword: string) => boolean;
  allUsers: User[];
  createUser: (user: Omit<User, 'id' | 'created_at'>) => void;
  updateUser: (id: string, updates: Partial<Omit<User, 'id' | 'created_at'>>) => void;
  deleteUser: (id: string) => void;
  getRoleLabel: (role: UserRole) => string;
  allRoles: UserRole[];
  permissionMap: Record<string, RolePermissions>;
  updateRolePermissions: (role: UserRole, perms: RolePermissions) => void;
  resetAllPermissions: () => void;
  // Dynamic role management
  customRoles: Record<string, CustomRoleData>;
  addCustomRole: (roleId: string, label: string) => boolean;
  removeCustomRole: (roleId: string) => boolean;
  isBuiltInRole: (roleId: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [usersDb, setUsersDb] = useState<User[]>(demoUsers);
  const [permissionMap, setPermissionMap] = useState<Record<string, RolePermissions>>(
    loadFromStorage<Record<string, RolePermissions>>(PERM_STORAGE_KEY, {})
  );
  const [customRoles, setCustomRoles] = useState<Record<string, CustomRoleData>>(
    loadFromStorage<Record<string, CustomRoleData>>(CUSTOM_ROLES_STORAGE_KEY, {})
  );

  // Derive final permission map: merge defaults with overrides (excluding custom roles which have their own)
  const effectivePermissionMap: Record<string, RolePermissions> = { ...DEFAULT_PERMISSIONS };
  for (const role of BUILT_IN_ROLES) {
    if (permissionMap[role]) {
      effectivePermissionMap[role] = permissionMap[role];
    }
  }
  // Add custom roles
  for (const [roleId, data] of Object.entries(customRoles)) {
    effectivePermissionMap[roleId] = data.permissions;
  }

  // Merge all roles
  const allRoles: UserRole[] = [...BUILT_IN_ROLES, ...Object.keys(customRoles)];

  const permissions = user ? effectivePermissionMap[user.role] || ALL_PERMISSIONS_ZERO : null;

  // Sync to localStorage whenever permissions or custom roles change
  useEffect(() => {
    // Only save built-in role overrides to permissions storage
    const builtInOverrides: Record<string, RolePermissions> = {};
    for (const role of BUILT_IN_ROLES) {
      if (permissionMap[role]) {
        builtInOverrides[role] = permissionMap[role];
      }
    }
    saveToStorage(PERM_STORAGE_KEY, builtInOverrides);
  }, [permissionMap]);

  useEffect(() => {
    saveToStorage(CUSTOM_ROLES_STORAGE_KEY, customRoles);
  }, [customRoles]);

  const login = useCallback((email: string, password: string): boolean => {
    const found = usersDb.find((u) => u.email === email && u.password === password);
    if (found) {
      setUser(found);
      return true;
    }
    return false;
  }, [usersDb]);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const changePassword = useCallback((oldPassword: string, newPassword: string): boolean => {
    if (!user) return false;
    if (user.password !== oldPassword) return false;
    setUsersDb((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, password: newPassword } : u))
    );
    setUser((prev) => (prev ? { ...prev, password: newPassword } : null));
    return true;
  }, [user]);

  const createUser = useCallback((newUser: Omit<User, 'id' | 'created_at'>) => {
    const userToAdd: User = {
      ...newUser,
      id: `usr-${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    setUsersDb((prev) => [...prev, userToAdd]);
  }, []);

  const updateUser = useCallback((id: string, updates: Partial<Omit<User, 'id' | 'created_at'>>) => {
    setUsersDb((prev) =>
      prev.map((u) => (u.id === id ? { ...u, ...updates } : u))
    );
    if (user && user.id === id) {
      setUser((prev) => (prev ? { ...prev, ...updates } : null));
    }
  }, [user]);

  const deleteUser = useCallback((id: string) => {
    setUsersDb((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const getRoleLabel = useCallback((role: UserRole): string => {
    if (BUILT_IN_LABELS[role]) return BUILT_IN_LABELS[role];
    if (customRoles[role]) return customRoles[role].label;
    // Fallback: format the role ID
    return role.charAt(0).toUpperCase() + role.slice(1).replace(/-/g, ' ');
  }, [customRoles]);

  const updateRolePermissions = useCallback((role: UserRole, perms: RolePermissions) => {
    if (customRoles[role]) {
      // Custom role — update in customRoles
      setCustomRoles((prev) => ({
        ...prev,
        [role]: { ...prev[role], permissions: perms },
      }));
    } else {
      // Built-in role — update in permissionMap
      setPermissionMap((prev) => ({ ...prev, [role]: perms }));
    }
  }, [customRoles]);

  const resetAllPermissions = useCallback(() => {
    setPermissionMap({});
    setCustomRoles({});
    localStorage.removeItem(PERM_STORAGE_KEY);
    localStorage.removeItem(CUSTOM_ROLES_STORAGE_KEY);
  }, []);

  const isBuiltInRole = useCallback((roleId: string): boolean => {
    return BUILT_IN_ROLES.includes(roleId);
  }, []);

  const addCustomRole = useCallback((roleId: string, label: string): boolean => {
    // Validate: must be lowercase, no spaces, 3-20 chars
    const cleanId = roleId.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    if (cleanId.length < 3 || cleanId.length > 20) return false;
    if (BUILT_IN_ROLES.includes(cleanId)) return false;
    if (customRoles[cleanId]) return false;

    setCustomRoles((prev) => ({
      ...prev,
      [cleanId]: {
        label,
        permissions: createEmptyPermissions(),
      },
    }));
    return true;
  }, [customRoles]);

  const removeCustomRole = useCallback((roleId: string): boolean => {
    if (BUILT_IN_ROLES.includes(roleId)) return false;
    if (!customRoles[roleId]) return false;

    // Check if any user has this role
    const usersWithRole = usersDb.filter((u) => u.role === roleId);
    if (usersWithRole.length > 0) return false; // Can't delete if users exist

    setCustomRoles((prev) => {
      const next = { ...prev };
      delete next[roleId];
      return next;
    });
    return true;
  }, [customRoles, usersDb]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        permissions,
        changePassword,
        allUsers: usersDb,
        createUser,
        updateUser,
        deleteUser,
        getRoleLabel,
        allRoles,
        permissionMap: effectivePermissionMap,
        updateRolePermissions,
        resetAllPermissions,
        customRoles,
        addCustomRole,
        removeCustomRole,
        isBuiltInRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}